import React, { useState } from "react";
import {
  BugOutlined,
  EditOutlined,
  ExperimentOutlined,
  MessageOutlined,
  NotificationOutlined,
  SyncOutlined,
  UserDeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Menu,
  message,
  Row,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import truncate from "lodash.truncate";
import uniq from "lodash.uniq";
import { useTranslation } from "react-i18next";

import { chatActions, traineeActions } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { AppAccessType } from "@fitness-app/data-models/entities/Automation";
import { TraineeStatus, type TraineeProfile } from "@fitness-app/data-models/entities/Trainee";
import { getAuthorName } from "@fitness-app/data-models/utils/getAuthorName";

import ModalForm from "~/components/ModalForm/ModalForm";
import { useIsPhone } from "~/hooks/useIsMobile";
import { usePermission, usePermissionScope } from "~/hooks/usePermission";
import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useEntityChange } from "~/hooks/useEntityChange";
import { useFormHandler } from "~/hooks/useFormHandler";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import {
  SupportRequestFormModal,
  type SupportRequestPrefill,
} from "~/modules/Support/components/SupportRequestFormModal";
import { SUPPORT_BOARD_FEATURE_FLAG } from "~/modules/Support/supportFeatureFlag";
import TraineeStatusBadge from "~/modules/Trainee/components/TraineeStatusBadge/TraineeStatusBadge";
import {
  SalesRepTagsForm,
  type SalesRepTagsFormModel,
} from "~/modules/Trainee/TraineeProfile/components/SalesRepTagsForm";
import TraineeAccess from "~/modules/Trainee/TraineeProfile/components/TraineeAccess";
import { TraineeAppVersion } from "~/modules/Trainee/TraineeProfile/components/TraineeAppVersion";
import TraineeRatingBadge from "~/modules/Trainee/TraineeProfile/components/TraineeRatingBadge";
import TraineeProfileForm from "~/modules/Trainee/TraineeProfile/TraineeProfileForm/TraineeProfileForm";
import { type TraineeProfileFormModel } from "~/modules/Trainee/TraineeProfile/TraineeProfileForm/types";
import { useSimulatorAccess } from "~/modules/Trainee/TraineeSimulator/simulatorAccess";
import TraineeSimulatorModal from "~/modules/Trainee/TraineeSimulator/TraineeSimulatorModal";
import { useTraineeProfile, useTraineeUserStatus } from "~/modules/Trainee/TraineeProfile/scope";
import { useTraineeLatestWeight } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/useTraineeMeasurementsQueries";
import { useAppDispatch } from "~/store/initializeStore";
import { useArchiveTrainee, useUpdateTrainee, useUpdateTraineeTags } from "./useTraineeProfileMutations";

const getClientAge = (birthday: string) => {
  return dayjs().diff(dayjs(birthday, "YYYY"), "years");
};

const ProfileInfo = () => {
  const isPhone = useIsPhone();
  const { data: profile } = useTraineeProfile();
  const { data: userStatus } = useTraineeUserStatus();
  // Po zmianie imienia nagłówek pokazuje dane z formularza zamiast danych z konta aplikacji.
  const [showUpdateProfileData, setShowUpdateProfileData] = useState(false);
  const { t } = useTranslation(["trainees"]);
  const { memberId } = useUserClaims();
  const { isTeamMember, isHeadCoach } = useUserRole();
  const canWriteClients = usePermission(PermissionKey.CLIENTS_WRITE);
  const canWriteChat = usePermission(PermissionKey.CHAT_WRITE);
  const canManageSales = usePermission(PermissionKey.SALES_MANAGE);
  const [showResendButton, toggleResendButton] = React.useState(true);
  // TRE-1636: sales reps edit tags via a dedicated RPC-backed modal instead of the profile form.
  const [showTagsModal, setShowTagsModal] = useState(false);
  const { openFormModal, showModalForm, hideFormModal, formModel } = useFormHandler<
    TraineeProfileFormModel,
    TraineeProfile
  >();
  const [loading, onSuccess, onError, onStart] = useEntityChange();
  const dispatch = useAppDispatch();
  const { weight: latestWeight } = useTraineeLatestWeight();
  const updateTrainee = useUpdateTrainee();
  const updateTags = useUpdateTraineeTags();
  const archiveTrainee = useArchiveTrainee();
  const { enabled: isSupportBoardEnabled } = usePostHogFeatureFlag(SUPPORT_BOARD_FEATURE_FLAG);
  const [supportRequestPrefill, setSupportRequestPrefill] = useState<SupportRequestPrefill | null>(null);
  // TRE-1761 — symulator działań klientki; hook sam pilnuje stagingu, flagi i roli właściciela.
  const { canUseSimulator } = useSimulatorAccess();
  const [showSimulator, setShowSimulator] = useState(false);

  // Edycja klientki spoza własnego przydziału zależy od zakresu clients.write.
  const canReadClients = usePermission(PermissionKey.CLIENTS_READ);
  const canReadAllClients = usePermission(PermissionKey.CLIENTS_READ, PermissionScope.ALL);
  const clientNotAssigned =
    usePermissionScope(PermissionKey.CLIENTS_WRITE) === PermissionScope.ASSIGNED &&
    profile?.assignedTrainerId !== memberId;

  if (!profile) {
    return <Card bodyStyle={{ padding: 0, minHeight: 300 }} style={{ height: "100%" }} loading />;
  }

  const shouldDisableOption = profile?.status === TraineeStatus.ARCHIVED && isTeamMember;
  const handleMainButton = () => {
    if (shouldDisableOption) {
      // resendInvite();
    } else {
      openFormModal(
        {
          firstName: profile.user?.firstName ?? profile.firstName ?? "",
          assignedTrainerId: profile.assignedTrainerId,
          lastName: profile.user?.lastName ?? profile.lastName ?? "",
          comment: profile.comment ?? "",
          tags: uniq(profile.tags ?? []),
          email: profile.email,
          gender: profile.gender,
          type: profile.type,
          phoneNumber: String(profile.metadata?.phoneNumber || profile.productClient?.phoneNumber || ""),
        },
        profile,
      );
    }
  };

  const onClientArchive = async () => {
    void message.loading({ content: "Archiwizujemy dane klienta...", key: "archiveClient", duration: 0 });
    try {
      await archiveTrainee.mutateAsync();
      void message.success("Pomyślnie zarchiwizowano klienta.");
    } catch {
      void message.error("Nie udało się zarchiwizować klienta. Spróbuj ponownie.");
    } finally {
      message.destroy("archiveClient");
    }
  };

  const resendInvite = async (traineeId: string, userId: string) => {
    if (!profile || !profile.productId) {
      void message.warning("Klient nie jest przypisany do żadnego produktu. Zgłoś problem do administratora.");
      return;
    }
    try {
      toggleResendButton(false);
      void message.loading({ duration: 0, key: "resendInvite", content: "Przetwarzam..." });
      await dispatch(traineeActions.resendInvitationToApp({ traineeId, userId })).unwrap();
      void message.success("E-mail z zaproszeniem do aplikacji i kodem logowania został wysłany.");
    } catch (e) {
      void message.error(String(e));
      toggleResendButton(true);
    } finally {
      message.destroy("resendInvite");
    }
  };

  const menu = (
    <Menu>
      {canWriteClients && profile.userId ? (
        <Menu.Item
          key="1"
          icon={<SyncOutlined />}
          onClick={() => {
            if (profile.userId) {
              void resendInvite(profile.id, profile.userId);
            }
          }}
        >
          {t("resendInvite")}
        </Menu.Item>
      ) : null}
      {isSupportBoardEnabled ? (
        <Menu.Item
          key="support"
          icon={<BugOutlined />}
          onClick={() =>
            setSupportRequestPrefill({
              trainee: {
                id: profile.id,
                label: `${[profile.firstName, profile.lastName].filter(Boolean).join(" ")} (${profile.email})`,
              },
              clientEmail: profile.email,
            })
          }
        >
          Utwórz zgłoszenie
        </Menu.Item>
      ) : null}
      {canUseSimulator ? (
        <Menu.Item key="simulator" icon={<ExperimentOutlined />} onClick={() => setShowSimulator(true)}>
          Symulator działań klientki
        </Menu.Item>
      ) : null}
      {!shouldDisableOption ? (
        <Menu.Item
          key="3"
          danger
          icon={<UserDeleteOutlined />}
          onClick={onClientArchive}
          disabled={!canWriteClients || Boolean(profile.productId && profile.productClient?.status === "active")}
        >
          {t("common:button.archive")}
        </Menu.Item>
      ) : null}
    </Menu>
  );

  const updateTraineeTags = async (model: SalesRepTagsFormModel) => {
    onStart();
    try {
      await updateTags.mutateAsync(uniq(model.tags ?? []));
      onSuccess(t<string>("messages.successfullyUpdatedProfile"));
      setShowTagsModal(false);
    } catch {
      onError();
    }
  };

  const updateTraineeProfile = async (model: TraineeProfileFormModel) => {
    onStart();
    const extended: TraineeProfileFormModel & { metadata?: TraineeProfile["metadata"] } = model;
    if (!extended.assignedTrainerId) {
      extended.assignedTrainerId = null;
    }

    if (extended.phoneNumber) {
      extended.metadata = {
        ...(profile.metadata || {}),
        phoneNumber: model.phoneNumber || null,
      };
    }

    delete extended.phoneNumber;

    try {
      await updateTrainee.mutateAsync(extended);
      if (extended.firstName && profile.user) {
        setShowUpdateProfileData(true);
      }
      onSuccess(t<string>("messages.successfullyUpdatedProfile"));
      hideFormModal();
    } catch {
      onError();
    }
  };

  return (
    <Card
      bodyStyle={{ padding: 0 }}
      id="profile-info"
      className="activity-card h-full"
      title={
        <Space size={6} align="center" wrap>
          {t("sections.basicInfo")}{" "}
          {profile.assignedTrainer ? (
            <div>
              (<span>{t("profile.trainer")}:</span> <Tag color="magenta">{getAuthorName(profile.assignedTrainer)}</Tag>)
            </div>
          ) : (
            <div className={profile.type === AppAccessType.Limited ? "hidden" : undefined}>
              <Tag color="red">{t("profile.lackOfTrainer")}</Tag>
            </div>
          )}
          {profile.status === TraineeStatus.ACTIVE && <TraineeRatingBadge traineeId={profile.id} />}
        </Space>
      }
      extra={
        shouldDisableOption ? null : canWriteClients ? (
          <Dropdown.Button size="small" onClick={handleMainButton} overlay={menu}>
            <EditOutlined /> {t("common:button.edit")}
          </Dropdown.Button>
        ) : canManageSales ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => setShowTagsModal(true)}>
            {t("profile.editTags", "Edytuj tagi")}
          </Button>
        ) : null
      }
    >
      <TraineeStatusBadge
        banner
        status={profile.status}
        extraAction={
          [TraineeStatus.INACTIVE, TraineeStatus.TERMINATED].includes(profile.status) &&
          canWriteClients &&
          profile.userId &&
          showResendButton ? (
            <Tooltip title={t("resendInvite")}>
              <Button
                className="ml-2"
                size="small"
                onClick={() => resendInvite(profile.id, profile.userId!)}
                icon={<SyncOutlined />}
              />
            </Tooltip>
          ) : undefined
        }
      />

      {profile.userId && profile.status === TraineeStatus.ACTIVE ? (
        <TraineeAppVersion userId={profile.userId} traineeId={profile.id} />
      ) : null}
      <Row gutter={[8, 8]} style={{ padding: 16 }} className="w-full">
        <Col xl={24} xxl={12} flex={1} className="w-full">
          <Space direction="vertical" align="center" className="w-full" size={16}>
            <Avatar
              icon={<UserOutlined />}
              size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 80 }}
              src={profile.user?.avatarUrl}
            />
            <div className="flex flex-col items-center gap-y-2">
              {showUpdateProfileData ? (
                <h3 className="capitalize">
                  {profile.firstName?.toLowerCase()} {profile.lastName?.toLowerCase()}
                </h3>
              ) : (
                <h3 className="capitalize">
                  {profile.user?.firstName?.toLowerCase() || profile.firstName?.toLowerCase()}{" "}
                  {profile.user?.lastName?.toLowerCase() ?? profile.lastName?.toLowerCase() ?? ""}
                </h3>
              )}
              <div className="flex flex-wrap justify-center gap-x-2 gap-y-1">
                <Typography.Paragraph
                  underline
                  copyable={{ tooltips: [t("copy"), t("copied")] }}
                  style={{ marginBottom: 0 }}
                >
                  {profile.email}
                </Typography.Paragraph>
                {(profile.metadata?.phoneNumber || profile.productClient?.phoneNumber) && (
                  <Typography.Paragraph
                    underline
                    copyable={{ tooltips: [t("copy"), t("copied")] }}
                    style={{ marginBottom: 0 }}
                  >
                    {profile.metadata?.phoneNumber || profile.productClient?.phoneNumber}
                  </Typography.Paragraph>
                )}
              </div>

              {/* Identyfikatory techniczne przydają się kontom operacyjnym przy zgłoszeniach. */}
              {canReadAllClients && (
                <Space wrap className="justify-center">
                  <Typography.Text
                    className="whitespace-nowrap"
                    copyable={{ tooltips: [profile.id, t("common:copied")], text: profile.id }}
                  >
                    Trainee id: {truncate(profile.id, { length: 10 })}
                  </Typography.Text>
                  {profile.userId && (
                    <Typography.Text
                      className="whitespace-nowrap"
                      copyable={{ tooltips: [profile.userId, t("common:copied")], text: profile.userId }}
                    >
                      User id: {truncate(profile.userId, { length: 10 })}
                    </Typography.Text>
                  )}
                </Space>
              )}
            </div>

            <div className="flex flex-col items-center gap-y-2">
              <Typography.Text type="secondary">
                {t("basicInfo.lastSeenAt")}:{" "}
                {userStatus?.lastChanged ? dayjs(userStatus.lastChanged).format("HH:mm DD.MM.YYYY") : "-"}
              </Typography.Text>
              <Typography.Text type="secondary">
                {t("basicInfo.added")}: {dayjs(profile.createdAt).format("DD.MM.YYYY")}
              </Typography.Text>
              {canReadClients && (
                <div>
                  <Typography.Text type="secondary">{t("profileForm.accessType")}:</Typography.Text>{" "}
                  <Tag color={profile.type === AppAccessType.FullAccess ? "success" : "warning"}>
                    {t(`accessType.${profile.type}`)}
                  </Tag>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-center  gap-y-2" style={{ marginBottom: 8 }}>
              {uniq(profile.tags || []).map((tag) => (
                <Tag color="blue" key={tag}>
                  {tag}
                </Tag>
              ))}
            </div>

            <Tooltip
              title={
                (!profile.userId &&
                  "Nie można wysłać wiadomości do tego użytkownika ponieważ nie potwierdził on Twojego zaproszenia.") ||
                (clientNotAssigned &&
                  "Nie można wysyłać wiadomości do tego użytkownika, ponieważ nie jest on przypisany do Ciebie")
              }
              placement="right"
            >
              <Dropdown.Button
                type="primary"
                // Archiwum czatu otwiera każdy, kto ma `chat.write` — kanał
                // zostaje do odczytu, a pisanie blokuje `ChatInput` po
                // `channel.status`. Dawne `shouldDisableOption` (ARCHIVED &&
                // isTeamMember, jeszcze z TRE-334) odcinało cały personel poza
                // właścicielem konta, mimo `chat.write` = Całe konto.
                disabled={
                  !canWriteChat || !profile.userId || clientNotAssigned || profile.status === TraineeStatus.INACTIVE
                }
                onClick={() =>
                  profile.userId
                    ? dispatch(
                        chatActions.findAndAddChannelToList({
                          userId: profile.userId,
                          isHeadCoach,
                        }),
                      )
                    : undefined
                }
                menu={{
                  items: [
                    {
                      key: "announcementChannel",
                      label: "Kanał ogłoszeniowy",
                      icon: <NotificationOutlined />,
                      onClick: () => {
                        if (!profile.userId) {
                          return;
                        }
                        void dispatch(
                          chatActions.findAndAddChannelToList({
                            userId: profile.userId,
                            isHeadCoach,
                            channelType: "marketing",
                          }),
                        )
                          .unwrap()
                          .catch(() => {
                            void message.warning(
                              "Klientka nie ma jeszcze kanału ogłoszeniowego. Utworzysz go w oknie czatu (menu → Kanał ogłoszeniowy).",
                            );
                          });
                      },
                    },
                  ],
                }}
              >
                <MessageOutlined />
                {t("profile.sendMessage")}
              </Dropdown.Button>
            </Tooltip>

            <Space
              direction="horizontal"
              size={isPhone ? 16 : 36}
              split={<Divider type="vertical" style={{ height: 35 }} />}
            >
              <Statistic
                title="Waga"
                valueStyle={{ whiteSpace: "nowrap" }}
                value={latestWeight ? `${latestWeight} kg` : "-"}
              />
              <Statistic
                title="Wzrost"
                valueStyle={{ whiteSpace: "nowrap" }}
                value={profile.height ? `${profile.height} cm` : "-"}
              />
              <Statistic
                title="Wiek"
                valueStyle={{ whiteSpace: "nowrap" }}
                value={profile.birthday ? `${getClientAge(profile.birthday)} lat` : "-"}
              />
            </Space>
          </Space>
        </Col>
        <Col xl={24} xxl={12} flex={1}>
          <TraineeAccess trainee={profile} shouldDisableOption={shouldDisableOption} />
        </Col>
        <ModalForm
          title={t("profileForm.title")}
          open={showModalForm}
          onCancel={hideFormModal}
          loading={Boolean(loading)}
        >
          <TraineeProfileForm onSubmit={updateTraineeProfile} model={formModel} />
        </ModalForm>
        <ModalForm
          title={t("profile.editTags", "Edytuj tagi")}
          open={showTagsModal}
          onCancel={() => setShowTagsModal(false)}
          loading={Boolean(loading)}
        >
          <SalesRepTagsForm onSubmit={updateTraineeTags} model={{ tags: uniq(profile.tags ?? []) }} />
        </ModalForm>
        {supportRequestPrefill && (
          <SupportRequestFormModal
            open
            prefill={supportRequestPrefill}
            onClose={() => setSupportRequestPrefill(null)}
          />
        )}
        {canUseSimulator && profile.id ? (
          <TraineeSimulatorModal traineeId={profile.id} open={showSimulator} onClose={() => setShowSimulator(false)} />
        ) : null}
      </Row>
    </Card>
  );
};

export default ProfileInfo;
